<script setup lang="ts">

import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { DURATION_UNLIMITED } from '@/types/ActionDuration.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps<{
  shipActionData: {
    action: string;
    targetX: number;
    targetY: number;
    targetPlayer: number;
    targetShip: number;
    targetDay?: number;
    duration?: number;
    data: {
      name: string;
      type: number;
      typeVariantId: number;
      targetPlayer: number;
    }
  },
  eta: number
}>();

const gameStore = useGameStore()

// Computed property for repair status text
const repairStatusText = computed(() => {
  // Handle new duration-based system
  if (props.shipActionData.duration !== undefined) {
    if (props.shipActionData.duration === DURATION_UNLIMITED) {
      return t('command.ships.current.repair.repairingUntilHealed')
    } else if (props.shipActionData.duration > 0) {
      return t('command.ships.current.repair.repairingForDays', { n: props.shipActionData.duration })
    } else {
      return t('command.ships.current.repair.repairComplete')
    }
  }

  // Legacy targetDay support (can be removed once backend is updated)
  if (props.shipActionData.targetDay) {
    const daysRemaining = props.shipActionData.targetDay - gameStore.turn
    if (daysRemaining > 0) {
      return t('command.ships.actions.repairingUntilDay', { day: props.shipActionData.targetDay, remaining: daysRemaining })
    } else {
      return t('command.ships.current.repair.repairComplete')
    }
  }

  return t('command.ships.current.repair.repairingUntilHealed')
})

// Computed property for repair duration display
const repairDurationDisplay = computed(() => {
  // Handle new duration-based system
  if (props.shipActionData.duration !== undefined) {
    if (props.shipActionData.duration === DURATION_UNLIMITED) {
      return "?"
    } else if (props.shipActionData.duration > 0) {
      return `${props.shipActionData.duration}`
    } else {
      return "0"
    }
  }
  
  // Legacy targetDay support
  if (props.shipActionData.targetDay) {
    const daysRemaining = props.shipActionData.targetDay - gameStore.turn
    return daysRemaining > 0 ? `${daysRemaining}` : "0"
  }
  
  return "?"
})

</script>

<template>
  <StandardCard top-padding="0" bottom-padding="1">
    <div class="flex justify-between text-subtext w-full">
      <div class="flex gap-4">
        <GenericIcon :tooltip="t('command.ships.current.repair.tooltip')" type="repair" class="hidden md:inline"/>
        <div>
          {{ repairStatusText }}
        </div>
      </div>
      <div class="flex-shrink-0 ps-2">
        {{ repairDurationDisplay }} {{ t('command.ships.current.days') }}
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">

</style>